import React from "react";
import { Tab, Typography } from "@mui/material";
import { TabList, TabPanel, TabContext } from "@mui/lab";

import { Widget } from "@/lib/api";
import { useDidMountEffect } from "@/features/shared/hooks";

import EditWidgetContainerMenu from "./edit-widget-container-menu";
import * as Styled from "./styled";
import styles from "./widget-container.module.css";

type Props = {
  widgets: Widget[];
  children?: React.ReactNode;
  pageId: string;
  containerId: string;
  /** Shown while the container is being resized, e.g. "6 × 10". */
  sizeLabel?: string;
  /** Subtle overlay when gutter resize has hit this container's min size. */
  isSquished?: boolean;
  /** Enables the bottom-right resize handle when provided. */
  onResizeStart?: (containerId: string, event: React.PointerEvent<HTMLElement>) => void;
};

const WidgetContainer: React.ForwardRefRenderFunction<HTMLDivElement, Props> = (
  {
    widgets,
    children,
    pageId,
    containerId,
    sizeLabel,
    isSquished,
    onResizeStart,
    ...restProps
  },
  ref,
) => {
  const [activeTabId, setActiveTabId] = React.useState(widgets[0].id);

  /*
    Widgets can be changed from outside of the widget container, e.g. when a widget is deleted.
    In this case we have to update the active tab id to the last widget in the list.
  */
  useDidMountEffect(() => {
    setActiveTabId(widgets[widgets.length - 1].id);
  }, [widgets]);

  const activeWidget = React.useMemo(
    () => widgets.find((widget) => widget.id === activeTabId),
    [activeTabId, widgets],
  );

  return (
    <Styled.Container ref={ref} {...restProps} elevation={0}>
      <TabContext value={activeTabId}>
        <Styled.InnerContainer>
          <Styled.Header>
            <div data-no-drag>
              <TabList onChange={(_, id) => setActiveTabId(id)}>
                {widgets.map((tab) => (
                  <Tab key={tab.id} label={tab.title} value={tab.id} />
                ))}
              </TabList>
            </div>
            {activeWidget && (
              <EditWidgetContainerMenu
                activeWidgetId={activeWidget.id}
                containerId={containerId}
                pageId={pageId}
              />
            )}
          </Styled.Header>
          {widgets.map((tab) => (
            <TabPanel key={tab.id} value={tab.id}>
              <Typography>{tab.title}</Typography>
              {/* TODO: Add widget preview */}
            </TabPanel>
          ))}
        </Styled.InnerContainer>
      </TabContext>
      {sizeLabel ? <div className={styles.sizeOverlay}>{sizeLabel}</div> : null}
      {isSquished ? (
        <div className={styles.squishOverlay} aria-hidden>
          <span className={styles.squishBadge}>min</span>
        </div>
      ) : null}
      {onResizeStart && (
        <div
          data-no-drag
          role="presentation"
          className={styles.resizeHandle}
          onPointerDown={(event) => onResizeStart(containerId, event)}
        />
      )}
      {/* We have to render the children to ensure the resize handlers are renderd */}
      {children}
    </Styled.Container>
  );
};

export default React.forwardRef(WidgetContainer);
